import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FaChartPie,
  FaClipboardList,
  FaClock,
  FaFileInvoice,
  FaFilter,
  FaSearch,
  FaTools,
  FaUserCheck
} from 'react-icons/fa';
import Navbar from '../Navbar/Navbar';
import api from '../Axios/axios';
import './TechnicalService.scss';

const STATUS_CODE_MAP = {
  New: 1,
  Assigned: 2,
  InProgress: 3,
  CompletedByPersonnel: 4,
  WaitingManagerApproval: 5,
  Approved: 6,
  SentToAccounting: 7,
  Cancelled: 8
};

const STATUS_TEXT = {
  1: 'Personel Ataması Bekliyor',
  2: 'Personele Atandı',
  3: 'Serviste',
  4: 'Personel Tamamladı',
  5: 'Bütçe Bekliyor',
  6: 'Müdür Onayladı',
  7: 'Muhasebeye Aktarıldı',
  8: 'İptal'
};

const SERVICE_LABELS = {
  1: 'Bakım',
  2: 'Parça Değişimi',
  3: 'Arıza Giderme',
  4: 'Montaj',
  5: 'Söküm',
  6: 'Kontrol',
  7: 'Diğer',
  maintenance: 'Bakım',
  partreplacement: 'Parça Değişimi',
  faultrepair: 'Arıza Giderme',
  assembly: 'Montaj',
  dismantling: 'Söküm',
  inspection: 'Kontrol',
  other: 'Diğer'
};

const normalizeStatus = (status) => {
  if (typeof status === 'number') return status;
  if (typeof status === 'string') {
    if (STATUS_CODE_MAP[status]) return STATUS_CODE_MAP[status];
    const parsed = Number(status);
    return Number.isNaN(parsed) ? 1 : parsed;
  }
  return 1;
};

const normalizeServiceTitle = (value) => {
  if (value === null || value === undefined) return '';
  const text = String(value).trim();
  if (!text || text === '0') return '';
  const normalized = text.replace(/[\s_-]/g, '');
  const keys = [text, normalized.toLowerCase(), normalized.toLocaleLowerCase('tr-TR')];
  const matchedKey = keys.find((key) => SERVICE_LABELS[key]);
  if (matchedKey) return SERVICE_LABELS[matchedKey];
  return text.toLocaleLowerCase('tr-TR').replace(/(^|\s|-)(\S)/g, (match) => match.toLocaleUpperCase('tr-TR'));
};

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Istanbul'
  }).format(new Date(value));
};

const formatMoney = (value) =>
  new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(Number(value || 0));

const getDayKey = (value) => {
  if (!value) return '';
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Europe/Istanbul'
  }).format(new Date(value));
};

const getRequestDate = (request) => request.operationDate || request.updatedDate || request.createdDate || '';
const getCompany = (request) => request.requestingCompanyName || request.companyName || request.externalCompanyName || 'Firma belirtilmedi';
const getLocation = (request) => request.workSiteLocation || request.externalWorkSiteName || request.workSiteName || '-';
const getSubject = (request) =>
  normalizeServiceTitle(request.serviceType) ||
  normalizeServiceTitle(request.serviceTypeId) ||
  normalizeServiceTitle(request.serviceSubject) ||
  normalizeServiceTitle(request.serviceTypeName) ||
  normalizeServiceTitle(request.subject) ||
  normalizeServiceTitle(request.requestTitle) ||
  'Teknik Servis';

const getCraneLabel = (crane) => {
  if (!crane) return '';
  const serial = crane.SerialNo || crane.serialNumber || crane.serialNo || crane.towerCraneSerialNo || crane.code || crane.Code || '';
  const brand = crane.Brand || crane.brand || crane.brandName || crane.make || crane.manufacturer || '';
  const model = crane.Model || crane.model || crane.Code || crane.modelName || crane.craneModel || crane.machineModel || '';
  const technicalName = [brand, model].filter(Boolean).join(' ').trim();
  return [serial, technicalName].filter(Boolean).join(' - ');
};

const getCraneOwner = (crane, companyById) => {
  if (!crane) return '';
  const direct =
    crane.ownerName ||
    crane.ownerCompanyName ||
    crane.towerCraneOwnerName ||
    crane.companyName ||
    crane.companyTitle ||
    crane.ownerTitle ||
    crane.company?.name ||
    crane.owner?.name;
  if (direct) return direct;
  return companyById.get(String(crane.ownerId))?.name || companyById.get(String(crane.companyId))?.name || '';
};

const getRequestCrane = (request, craneById) => {
  const craneId = request.TowerCraneId || request.towerCraneId || request.craneId || request.machineId;
  return craneById.get(String(craneId)) || null;
};

const getMachine = (request, craneById) => {
  const craneLabel = getCraneLabel(getRequestCrane(request, craneById));
  const direct =
    request.machineLabel ||
    request.SerialNo ||
    request.serialNo ||
    request.towerCraneSerialNo ||
    request.towerCraneSerial ||
    request.machineSerialNo ||
    request.machineSerialNumber ||
    request.machineCode ||
    request.towerCraneModel ||
    request.machineModel ||
    request.machineName ||
    request.towerCraneName ||
    request.externalMachineName ||
    request.externalMachineModel;
  if (direct && craneLabel && !String(direct).includes(craneLabel)) {
    const directText = String(direct).trim();
    const craneParts = craneLabel.split(' - ').map((part) => part.trim()).filter(Boolean);
    const extraParts = craneParts.filter((part) => part && !directText.toLocaleLowerCase('tr-TR').includes(part.toLocaleLowerCase('tr-TR')));
    return [directText, ...extraParts].filter(Boolean).join(' - ');
  }
  return direct || craneLabel || 'Makine belirtilmedi';
};

const getPersonnel = (request) => {
  const assignments = (request.assignments || [])
    .map((assignment) => assignment.userName || assignment.assignedUserName || assignment.fullName)
    .filter(Boolean)
    .join(', ');
  const workLogs = (request.workLogs || [])
    .map((log) => log.userName || log.createdByName)
    .filter(Boolean)
    .join(', ');
  return assignments || workLogs || request.assignedPersonnelName || request.assignedTeam || request.personnelName || 'Atama bekliyor';
};

const sumRequestBudget = (request) => {
  if (Number(request.totalPrice || 0) > 0) return Number(request.totalPrice || 0);
  const logs = request.workLogs || [];
  return logs.reduce((sum, log) => {
    const partTotal = (log.usedParts || []).reduce((partSum, part) => {
      return partSum + Number(part.quantity || 0) * Number(part.unitPrice || 0);
    }, 0);
    return sum + partTotal;
  }, 0);
};

const getUploadUrl = (fileName) => {
  if (!fileName) return '#';
  if (/^https?:\/\//i.test(fileName)) return fileName;
  const root = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');
  const clean = String(fileName).replace(/^\/+/, '');
  return clean.toLowerCase().startsWith('uploads/') ? `${root}/${clean}` : `${root}/uploads/${clean}`;
};

const REQUESTS_PER_PAGE = 13;

const TechnicalServiceAdminDashboardPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedStatus = searchParams.get('status');
  const routeStatus = ['open', '1', '2', '3', '4', '5', '6', '7', '8'].includes(requestedStatus) ? requestedStatus : '';
  const [requests, setRequests] = useState([]);
  const [cranes, setCranes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: routeStatus,
    service: '',
    company: '',
    personnel: '',
    startDate: '',
    endDate: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  useEffect(() => {
    setFilters(previous => ({ ...previous, status: routeStatus }));
  }, [routeStatus]);
  const changeStatusFilter = (value) => {
    setFilters(previous => ({ ...previous, status: value }));
    const next = new URLSearchParams(searchParams);
    if (value) next.set('status', value);
    else next.delete('status');
    setSearchParams(next);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [requestRes, craneRes, companyRes] = await Promise.all([
        api.get('/TechnicalService/requests'),
        api.get('/TowerCrane').catch(() => ({ data: { data: [] } })),
        api.get('/Company').catch(() => ({ data: { data: [] } }))
      ]);
      setRequests(requestRes?.data?.data || []);
      setCranes(craneRes?.data?.data || []);
      setCompanies(companyRes?.data?.data || []);
      setErrorMessage('');
    } catch (error) {
      console.error('Teknik servis yönetici paneli yüklenemedi:', error);
      setErrorMessage('Teknik servis verileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const companyById = useMemo(() => {
    const map = new Map();
    companies.forEach((company) => {
      const id = company?.id ?? company?.Id;
      if (id !== undefined && id !== null) map.set(String(id), company);
    });
    return map;
  }, [companies]);

  const craneById = useMemo(() => {
    const map = new Map();
    cranes.forEach((crane) => {
      const id = crane?.id ?? crane?.Id;
      if (id !== undefined && id !== null) map.set(String(id), crane);
    });
    return map;
  }, [cranes]);

  const rows = useMemo(() => {
    return requests
      .map((request) => {
        const crane = getRequestCrane(request, craneById);
        const statusCode = normalizeStatus(request.status);
        const budgetTotal = sumRequestBudget(request);
        return {
          ...request,
          statusCode,
          statusText: STATUS_TEXT[statusCode] || 'Bilinmiyor',
          subjectText: getSubject(request),
          companyText: getCompany(request),
          locationText: getLocation(request),
          machineText: getMachine(request, craneById),
          ownerText: getCraneOwner(crane, companyById) || 'Vinç sahibi belirtilmedi',
          personnelText: getPersonnel(request),
          budgetTotal,
          requestDate: getRequestDate(request)
        };
      })
      .sort((a, b) => new Date(b.requestDate || 0) - new Date(a.requestDate || 0));
  }, [requests, craneById, companyById]);

  const uniquePersonnel = useMemo(
    () => Array.from(new Set(rows.map((row) => row.personnelText).filter((name) => name && name !== 'Atama bekliyor'))).sort((a, b) => a.localeCompare(b, 'tr')),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const search = filters.search.trim().toLocaleLowerCase('tr-TR');
    return rows.filter((row) => {
      const haystack = [
        row.requestNo,
        row.subjectText,
        row.companyText,
        row.ownerText,
        row.machineText,
        row.locationText,
        row.personnelText,
        row.statusText
      ].join(' ').toLocaleLowerCase('tr-TR');
      const matchesSearch = !search || haystack.includes(search);
      const matchesStatus = !filters.status || (filters.status === 'open' ? [1, 2, 3, 4, 5].includes(row.statusCode) : String(row.statusCode) === filters.status);
      const matchesService = !filters.service || row.subjectText === filters.service;
      const matchesCompany = !filters.company || row.companyText.toLocaleLowerCase('tr-TR').includes(filters.company.toLocaleLowerCase('tr-TR'));
      const matchesPersonnel = !filters.personnel || row.personnelText === filters.personnel;
      const rowDate = row.requestDate ? getDayKey(row.requestDate) : '';
      const afterStart = !filters.startDate || rowDate >= filters.startDate;
      const beforeEnd = !filters.endDate || rowDate <= filters.endDate;
      return matchesSearch && matchesStatus && matchesService && matchesCompany && matchesPersonnel && afterStart && beforeEnd;
    });
  }, [rows, filters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / REQUESTS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedRows = filteredRows.slice((safePage - 1) * REQUESTS_PER_PAGE, safePage * REQUESTS_PER_PAGE);

  const stats = useMemo(() => {
    const assigned = rows.filter((row) => row.statusCode === 2);
    const inProgress = rows.filter((row) => row.statusCode === 3);
    const budget = rows.filter((row) => row.statusCode === 4 || row.statusCode === 5);
    const accounting = rows.filter((row) => row.statusCode === 7);
    return [
      { label: 'Toplam Talep', value: rows.length, icon: FaClipboardList, tone: 'blue' },
      { label: 'Atama Bekleyen', value: rows.filter((row) => row.statusCode === 1).length, icon: FaClock, tone: 'orange' },
      { label: 'Personele Atanan', value: assigned.length, icon: FaUserCheck, tone: 'blue' },
      { label: 'Devam Eden', value: inProgress.length, icon: FaTools, tone: 'green' },
      { label: 'Bütçe Bekleyen', value: budget.length, icon: FaChartPie, tone: 'orange' },
      { label: 'Muhasebeye Aktarılan', value: accounting.length, icon: FaFileInvoice, tone: 'blue' }
    ];
  }, [rows]);

  const totalBudget = useMemo(() => rows.reduce((sum, row) => sum + row.budgetTotal, 0), [rows]);
  const completedPersonnel = rows.filter((row) => row.statusCode >= 4 && row.statusCode !== 8);
  const invoicedCount = rows.filter((row) => row.invoiceRecord || row.invoiceNo || row.invoiceNumber || row.isInvoiced).length;

  const distribution = useMemo(() => {
    const items = [
      { label: 'Açık Talep', value: rows.filter((row) => row.statusCode === 1).length, color: '#0d6efd' },
      { label: 'Serviste', value: rows.filter((row) => row.statusCode === 2 || row.statusCode === 3).length, color: '#20b26b' },
      { label: 'Bütçe Bekleyen', value: rows.filter((row) => row.statusCode === 4 || row.statusCode === 5).length, color: '#f5a623' },
      { label: 'Muhasebeye Aktarılan', value: rows.filter((row) => row.statusCode === 7).length, color: '#7c5cff' }
    ];
    const total = items.reduce((sum, item) => sum + item.value, 0) || 1;
    let current = 0;
    const gradient = items
      .map((item) => {
        const start = current;
        const end = current + (item.value / total) * 100;
        current = end;
        return `${item.color} ${start}% ${end}%`;
      })
      .join(', ');
    return { items, gradient };
  }, [rows]);

  const serviceDistribution = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => map.set(row.subjectText, (map.get(row.subjectText) || 0) + 1));
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [rows]);

  const personnelDistribution = useMemo(() => {
    const map = new Map();
    completedPersonnel.forEach((row) => {
      const name = row.personnelText || 'Personel belirtilmedi';
      map.set(name, (map.get(name) || 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [completedPersonnel]);

  const openDetail = async (request) => {
    if (!request) return;
    try {
      setDetail(request);
      setDetailLoading(true);
      const response = await api.get(`/TechnicalService/requests/${request.id}`, { skipCache: true });
      const item = response?.data?.data || request;
      const crane = getRequestCrane(item, craneById);
      const statusCode = normalizeStatus(item.status ?? request.status);
      setDetail({
        ...request,
        ...item,
        statusCode,
        statusText: STATUS_TEXT[statusCode] || 'Bilinmiyor',
        subjectText: getSubject(item),
        companyText: getCompany(item),
        locationText: getLocation(item),
        machineText: getMachine(item, craneById),
        ownerText: getCraneOwner(crane, companyById) || request.ownerText,
        personnelText: getPersonnel(item),
        budgetTotal: sumRequestBudget(item),
        requestDate: getRequestDate(item)
      });
    } catch (error) {
      console.error('Teknik servis detay alınamadı:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const renderDetailModal = () => {
    if (!detail) return null;
    const workLogs = detail.workLogs || [];
    const usedParts = workLogs.flatMap((log) => log.usedParts || []);
    const files = workLogs.flatMap((log) => (log.files || []).map((file) => ({ ...file, workLogUserName: log.userName || log.createdByName })));

    return (
      <div className="ts-admin-modal-backdrop" onClick={() => setDetail(null)}>
        <section className="ts-admin-modal" onClick={(event) => event.stopPropagation()}>
          <header>
            <div>
              <span>{detail.requestNo || 'Teknik Servis Talebi'}</span>
              <h2>{detail.subjectText}</h2>
              <p>{detail.statusText} · {formatDateTime(detail.requestDate)}</p>
            </div>
            <button type="button" onClick={() => setDetail(null)}>Kapat</button>
          </header>
          {detailLoading ? (
            <p className="ts-admin-empty">Talep detayı yükleniyor...</p>
          ) : (
            <>
              <div className="ts-admin-detail-grid">
                <article><span>Firma</span><strong>{detail.companyText}</strong></article>
                <article><span>Vinç Sahibi</span><strong>{detail.ownerText}</strong></article>
                <article><span>Makine</span><strong>{detail.machineText}</strong></article>
                <article><span>Konum</span><strong>{detail.locationText}</strong></article>
                <article><span>Personel</span><strong>{detail.personnelText}</strong></article>
                <article><span>Bütçe</span><strong>{formatMoney(detail.budgetTotal)}</strong></article>
                <article><span>Muhasebe</span><strong>{detail.isSentToAccounting || detail.statusCode === 7 ? 'Aktarıldı' : 'Aktarılmadı'}</strong></article>
                <article><span>Fatura</span><strong>{detail.invoiceNo || detail.invoiceNumber || detail.invoiceRecord?.invoiceNo || 'Kesilmedi'}</strong></article>
              </div>

              <div className="ts-admin-modal-section">
                <h3>Personel İşlem Notları</h3>
                {workLogs.length === 0 ? <p>Personel işlem kaydı bulunmuyor.</p> : workLogs.map((log) => (
                  <article key={log.id || log.processDate} className="ts-admin-log">
                    <strong>{log.userName || log.createdByName || 'Teknik servis personeli'}</strong>
                    <time>{formatDateTime(log.processDate)}</time>
                    <p>{log.description || log.processDescription || 'Açıklama girilmemiş.'}</p>
                  </article>
                ))}
              </div>

              <div className="ts-admin-modal-section">
                <h3>Bütçe Kalemleri</h3>
                {usedParts.length === 0 ? <p>Parça, işçilik veya servis kalemi bulunmuyor.</p> : usedParts.map((part) => (
                  <article key={part.id || `${part.itemName}-${part.unitPrice}`} className="ts-admin-part">
                    <strong>{part.itemName}</strong>
                    <span>{part.quantity || 1} {part.unit || 'Adet'}</span>
                    <em>{formatMoney(part.unitPrice)}</em>
                  </article>
                ))}
                {detail.managerNote && <p className="ts-admin-manager-note"><strong>Müdür Notu:</strong> {detail.managerNote}</p>}
              </div>

              <div className="ts-admin-modal-section">
                <h3>Fotoğraflar ve Dosyalar</h3>
                {files.length === 0 ? <p>Yüklenmiş fotoğraf veya dosya bulunmuyor.</p> : (
                  <div className="ts-admin-file-grid">
                    {files.map((file) => {
                      const fileName = file.fileName || file.path || file.url || '';
                      const isImage = String(fileName).match(/\.(jpg|jpeg|png|webp)$/i);
                      return (
                        <a key={file.id || fileName} href={getUploadUrl(fileName)} target="_blank" rel="noreferrer">
                          {isImage ? <img src={getUploadUrl(fileName)} alt="Teknik servis dosyası" /> : <FaFileInvoice />}
                          <strong>{Number(file.fileType) === 4 ? 'Makine Fotoğrafı' : 'Teknik Servis Formu'}</strong>
                          <span>{file.workLogUserName || 'Personel'}</span>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    );
  };

  return (
    <>
      <Navbar />
      <main className="ts-admin-page">
        <section className="ts-admin-hero">
          <div>
            <span>Genel Kontrol</span>
            <h1>Teknik Servis Kontrol Paneli</h1>
            <p>Talep akışı, personel, bütçe, muhasebe ve fatura durumlarını tek ekrandan takip edin.</p>
          </div>
          <div className="ts-admin-hero-metrics">
            <article><strong>{formatMoney(totalBudget)}</strong><span>Toplam Bütçe</span></article>
            <article><strong>{completedPersonnel.length}</strong><span>Personel Tamamladı</span></article>
            <article><strong>{invoicedCount}</strong><span>Faturası Kesilen</span></article>
          </div>
        </section>

        {errorMessage && <div className="technical-service-alert error">{errorMessage}</div>}

        <section className="ts-admin-stats">
          {stats.map((item) => {
            const Icon = item.icon;
            return (
              <article key={item.label} className={`ts-admin-stat ${item.tone}`}>
                <div><span>{item.label}</span><strong>{loading ? '-' : item.value}</strong></div>
                <Icon />
              </article>
            );
          })}
        </section>

        <section className="ts-admin-filters">
          <div className="ts-admin-filter-title"><FaFilter /><strong>Filtrele</strong></div>
          <label className="ts-admin-search"><FaSearch /><input value={filters.search} onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))} placeholder="Talep no, firma, makine, personel veya konum ara" /></label>
          <select value={filters.status} onChange={(e) => changeStatusFilter(e.target.value)}>
            <option value="">Tüm Durumlar</option>
            <option value="open">Açık Servis İşleri</option>
            {Object.entries(STATUS_TEXT).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <select value={filters.service} onChange={(e) => setFilters((p) => ({ ...p, service: e.target.value }))}>
            <option value="">Tüm Servis Konuları</option>
            {Array.from(new Set(rows.map((row) => row.subjectText))).sort((a, b) => a.localeCompare(b, 'tr')).map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <select value={filters.personnel} onChange={(e) => setFilters((p) => ({ ...p, personnel: e.target.value }))}>
            <option value="">Tüm Personeller</option>
            {uniquePersonnel.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <input type="date" value={filters.startDate} onChange={(e) => setFilters((p) => ({ ...p, startDate: e.target.value }))} />
          <input type="date" value={filters.endDate} onChange={(e) => setFilters((p) => ({ ...p, endDate: e.target.value }))} />
        </section>

        <section className="ts-admin-layout">
          <div className="ts-admin-card ts-admin-table-card">
            <div className="ts-admin-card-head">
              <div>
                <h2>Teknik Servis Talepleri</h2>
                <p>{filteredRows.length} kayıt listeleniyor. Satıra tıklayarak detayları açabilirsiniz.</p>
              </div>
              <button type="button" onClick={loadData}>Yenile</button>
            </div>
            <div className="ts-admin-table">
              <div className="ts-admin-table-head">
                <span>Talep</span>
                <span>Firma / Vinç</span>
                <span>Personel</span>
                <span>Operasyon</span>
                <span>Durum</span>
                <span>Bütçe</span>
              </div>
              {pagedRows.map((row) => (
                <button key={row.id} type="button" className="ts-admin-row" onClick={() => openDetail(row)}>
                  <span><strong>{row.requestNo || 'TS'}</strong><em>{row.subjectText}</em></span>
                  <span title={`${row.companyText} / ${row.machineText} / ${row.ownerText}`}>
                    <strong>{row.companyText}</strong>
                    <em>{row.machineText} / {row.ownerText}</em>
                  </span>
                  <span className="ts-admin-row-personnel">{row.personnelText}</span>
                  <span className="ts-admin-row-date">{formatDateTime(row.requestDate)}</span>
                  <span className={`ts-admin-status status-${row.statusCode}`}>{row.statusText}</span>
                  <span>{formatMoney(row.budgetTotal)}</span>
                </button>
              ))}
              {!loading && filteredRows.length === 0 && <p className="ts-admin-empty">Filtreye uygun teknik servis talebi bulunamadı.</p>}
            </div>
            {filteredRows.length > REQUESTS_PER_PAGE && (
              <div className="ts-admin-pagination">
                <button
                  type="button"
                  className="ts-admin-pagination-arrow"
                  aria-label="Onceki sayfa"
                  disabled={safePage === 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                >
                  &lsaquo;
                </button>
                <span>Sayfa {safePage} / {totalPages}</span>
                <button
                  type="button"
                  className="ts-admin-pagination-arrow"
                  aria-label="Sonraki sayfa"
                  disabled={safePage === totalPages}
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                >
                  &rsaquo;
                </button>
              </div>
            )}
          </div>

          <aside className="ts-admin-side">
            <section className="ts-admin-card">
              <h2>Talep Dağılımı</h2>
              <div className="ts-admin-donut" style={{ background: `conic-gradient(${distribution.gradient})` }}>
                <div><strong>{rows.length}</strong><span>Toplam</span></div>
              </div>
              <div className="ts-admin-legend">
                {distribution.items.map((item) => <p key={item.label}><i style={{ background: item.color }} />{item.label}<strong>{item.value}</strong></p>)}
              </div>
            </section>
            <section className="ts-admin-card">
              <h2>Servis Konuları</h2>
              <div className="ts-admin-bars">
                {serviceDistribution.map(([label, value]) => (
                  <article key={label}>
                    <div><span>{label}</span><strong>{value}</strong></div>
                    <em style={{ width: `${Math.max(8, (value / Math.max(rows.length, 1)) * 100)}%` }} />
                  </article>
                ))}
              </div>
            </section>
            <section className="ts-admin-card">
              <h2>Personel Performansı</h2>
              <div className="ts-admin-personnel-list">
                {personnelDistribution.length === 0 ? <p>Henüz tamamlanan iş yok.</p> : personnelDistribution.map(([label, value]) => (
                  <article key={label}><span>{label}</span><strong>{value} iş</strong></article>
                ))}
              </div>
            </section>
          </aside>
        </section>
      </main>
      {renderDetailModal()}
    </>
  );
};

export default TechnicalServiceAdminDashboardPage;
